import { action, makeAutoObservable } from "mobx";
import { EffectDescription, FilterDescription, TitleDescription, TransitionDescription } from "../config/config";
import { CompositionService } from "../services/CompositionService";
import { CompositionStateStore } from "./CompositionStateStore";
import type { ScriptChunkV1 } from "../types/ScriptChunk";
import {
    buildScriptTimelineModel,
    findScriptTimelineSectionAtTime,
    getScriptTimelineSelectionRange,
    type ScriptTimelineSection,
} from "../utils/scriptTimelineSections";

export type ThemeVariants = "dark" | "light" | "system";

/**
 * Reference to a visual element on the canvas.
 * For regular clips: just clipId
 * For ScriptLayerClip: clipId + sourceIndex to identify specific video source
 */
export interface VisualElementRef {
    clipId: string;
    sourceIndex?: number;  // undefined for regular clips, number for ScriptLayerClip video sources
}

/**
 * Crop configuration for a visual element
 */
export interface CropConfig {
    /** X offset of the crop region (0-1, relative to original width) */
    x: number;
    /** Y offset of the crop region (0-1, relative to original height) */
    y: number;
    /** Width of crop region (0-1, relative to original width) */
    width: number;
    /** Height of crop region (0-1, relative to original height) */
    height: number;
}

export type CropScope = 'current' | 'all';
export type SceneEditScope = 'scene' | 'all';
export type PendingInsertIntoScriptMode = "insert_into_script" | "add_to_scene";
export const PENDING_SCRIPT_MEDIA_REQUEST_MAX_AGE_MS = 5 * 60_000;

export interface PendingInsertIntoScriptContext {
    requestId: string;
    atClipLocalSec: number;
    atTimelineSec?: number;
    preferExistingEmptyScene?: boolean;
    mode?: PendingInsertIntoScriptMode;
    source?: string;
    sceneIndex?: number;
    sceneStartSec?: number;
    sceneEndSec?: number;
    expectedScriptClipId?: string;
    expectedCompositionSessionVersion?: number;
    expectedScriptTimingFingerprint?: string;
    createdAtMs: number;
}

type DraggingObjectPayload =
    | {
    type: "media";
    payload: any;
}
    | {
    type: "text-preset";
    payload: TitleDescription;
};

class ApplicationStoreInstance {
    isLoading: boolean = true;
    isRendering: boolean = false;
    selectedClipId: string | null = null;
    selectedVisualElement: VisualElementRef | null = null;  // For canvas visual element selection
    selectedTransitionId: string | null = null;
    editingTextClipId: string | null = null;
    subtitlesClipId: string | null = null;
    transitions: Record<string, TransitionDescription> = {};
    filters: Record<string, FilterDescription> = {};
    effects: Record<string, EffectDescription> = {};
    draggingObject: DraggingObjectPayload | null = null;
    selectedRange: {start: number; end: number} | null = null;
    rangeToolEnabled: boolean = false;
    slipToolEnabled: boolean = false;

    selectedScriptLayerRange: {start: number; end: number} | null = null;
    selectedTranscriptTimelineRange: {start: number; end: number} | null = null;
    selectedScriptSection: ScriptTimelineSection | null = null;
    scriptTranscriptSelectionRequest: {
        range: {start: number; end: number} | null;
        revision: number;
    } = { range: null, revision: 0 };
    selectedSceneIndex: number | null = null;  // Index of selected scene in script clip (0-based)
    theme: ThemeVariants = "system";

    // Crop mode state
    isCropMode: boolean = false;
    cropScope: CropScope = 'current';
    // Scene edit scope for per-scene overrides (visual properties, captions styles, etc.)
    sceneEditScope: SceneEditScope = 'scene';
    /** Working crop config while in crop mode (before "Done" is clicked) */
    workingCrop: CropConfig = { x: 0, y: 0, width: 1, height: 1 };
    /** Zoom level for display in crop mode (1 = 100%) */
    cropZoom: number = 1;
    filtersPath: string | null = null;
    effectsPath: string | null = null;
    transitionsPath: string | null = null;
    titlesPath: string | null = null;
    subtitleStylesPath: string | null = null;
    animationsPath: string | null = null;

    // Pending "Add layer" insertion context (e.g., from timeline context menu).
    // Used to anchor insertion to a specific time and apply constraints like "end at scene end".
    pendingAddLayerContext: { startAtSec: number; constrainToSceneEnd?: boolean; createdAtMs: number } | null = null;

    // Pending "Add layer from transcript selection" context.
    // The transcript selection is in script-clip local time (same domain as WordNode payloads).
    // We keep both boundaries so the media selection can:
    // 1) Split the script into a scene spanning the selection
    // 2) Insert a layer constrained to exactly that scene range
    pendingAddLayerFromSelectionContext:
        | { startSec: number; endSec: number; createdAtMs: number }
        | null = null;

    // Pending "Replace placeholder" context (e.g., from placeholder clip context menu).
    // Used to route the next media selection from the Project sidebar into a placeholder slot.
    pendingPlaceholderReplaceContext: { placeholderClipId: string; createdAtMs: number } | null = null;

    // Pending "Change layout" context (e.g., from Scene bar context menu).
    // Used to open the Layout picker in the Properties sidebar for a specific scene.
    pendingSceneLayoutPickerContext: { sceneIndex: number; createdAtMs: number } | null = null;

    // One-shot request to auto-enable Studio Sound for a specific clip when the
    // Properties sidebar opens it. Used by enhance-audio quick start handoff.
    pendingStudioSoundAutoEnableClipId: string | null = null;

    // Pending "Insert media into script" context (e.g., from transcript empty-line overlay).
    // Used to route the next media selection from the Project sidebar into the script spine at a clip-local time.
    pendingInsertIntoScriptContext: PendingInsertIntoScriptContext | null = null;

    // Pending "Add Scene" (split selection into its own scene) context.
    // Set by the transcript toolbar, consumed by the Layout picker when a layout is chosen.
    pendingAddSceneFromSelectionContext:
        | { startSec: number; endSec: number; createdAtMs: number }
        | null = null;

    // Internal transcript "script section" clipboard (copy/paste).
    // Stores a chunk of the ScriptLayerClip spine (sources + scene props) captured from a transcript selection.
    scriptChunkClipboard: { chunk: ScriptChunkV1; createdAtMs: number } | null = null;

    // Editor-wide settings (in-memory for now; will move to persisted editor settings).
    applySmartTransitionsToNewScenes: boolean = true;

    constructor() {
        makeAutoObservable(this);
    }

    setApplySmartTransitionsToNewScenes(value: boolean) {
        this.applySmartTransitionsToNewScenes = Boolean(value);
    }

    setIsLoading(value: boolean) {
        this.isLoading = value;
    }

    setIsRendering(value: boolean) {
        this.isRendering = value;
    }

    setSelectedClipId(id: string | null) {
        // Clear previous selection ranges when switching clips
        if (this.selectedClipId !== id) {
            this.selectedScriptLayerRange = null;
            this.selectedTranscriptTimelineRange = null;
            this.selectedScriptSection = null;
            this.scriptTranscriptSelectionRequest = {
                range: null,
                revision: this.scriptTranscriptSelectionRequest.revision + 1,
            };
            this.selectedSceneIndex = null;
            this.selectedRange = null;
            this.editingTextClipId = null;
        }
        this.selectedClipId = id;
        this.setSelectedTransitionId(null);

        // Only set selectedVisualElement if the clip actually has visual elements
        // AudioClips don't have visual elements; VideoClip and ImageClip always do
        // ScriptLayerClip may or may not have video sources
        if (id) {
            const clip = CompositionService.getClipById(id);
            const clipType = (clip as any)?.type;

            let hasVisual = false;
            if (clipType === 'video' || clipType === 'image' || clipType === 'captions' || clipType === 'text' || clipType === 'placeholder') {
                hasVisual = true;
            } else if (clipType === 'script') {
                // ScriptLayerClip only has visual if it has video entries
                const videoEntries = (clip as any)?.getVideoEntries?.() ?? [];
                hasVisual = videoEntries.length > 0;
            }
            // audio clips have no visual elements

            this.selectedVisualElement = hasVisual
                ? clipType === 'script'
                    ? { clipId: id, sourceIndex: 0 }
                    : { clipId: id }
                : null;
        } else {
            this.selectedVisualElement = null;
        }
    }

    clearSelectedClipId() {
        this.selectedClipId = null;
        this.selectedVisualElement = null;
        this.selectedScriptLayerRange = null;
        this.selectedTranscriptTimelineRange = null;
        this.selectedScriptSection = null;
        this.scriptTranscriptSelectionRequest = {
            range: null,
            revision: this.scriptTranscriptSelectionRequest.revision + 1,
        };
        this.selectedSceneIndex = null;
        this.selectedRange = null;
        this.editingTextClipId = null;
        this.exitCropMode();
    }

    setPendingAddLayerContext(ctx: { startAtSec: number; constrainToSceneEnd?: boolean } | null) {
        if (!ctx) {
            this.pendingAddLayerContext = null;
            return;
        }
        // Only one "pending sidebar action" should be active at a time.
        this.pendingAddLayerFromSelectionContext = null;
        this.pendingPlaceholderReplaceContext = null;
        this.pendingInsertIntoScriptContext = null;
        this.pendingAddSceneFromSelectionContext = null;
        const startAtSec = Number(ctx.startAtSec ?? 0);
        this.pendingAddLayerContext = {
            startAtSec: Number.isFinite(startAtSec) ? startAtSec : 0,
            constrainToSceneEnd: Boolean(ctx.constrainToSceneEnd),
            createdAtMs: Date.now(),
        };
    }

    consumePendingAddLayerContext(maxAgeMs: number = 60_000): { startAtSec: number; constrainToSceneEnd?: boolean } | null {
        const ctx = this.pendingAddLayerContext;
        this.pendingAddLayerContext = null;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) return null;
        return { startAtSec: ctx.startAtSec, constrainToSceneEnd: ctx.constrainToSceneEnd };
    }

    peekPendingAddLayerContext(maxAgeMs: number = 60_000): { startAtSec: number; constrainToSceneEnd?: boolean } | null {
        const ctx = this.pendingAddLayerContext;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) return null;
        return { startAtSec: ctx.startAtSec, constrainToSceneEnd: ctx.constrainToSceneEnd };
    }

    setPendingAddLayerFromSelectionContext(ctx: { startSec: number; endSec: number } | null) {
        if (!ctx) {
            this.pendingAddLayerFromSelectionContext = null;
            return;
        }
        // Only one "pending sidebar action" should be active at a time.
        this.pendingAddLayerContext = null;
        this.pendingPlaceholderReplaceContext = null;
        this.pendingInsertIntoScriptContext = null;
        this.pendingAddSceneFromSelectionContext = null;

        const startSec = Number(ctx.startSec);
        const endSec = Number(ctx.endSec);
        if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec) {
            this.pendingAddLayerFromSelectionContext = null;
            return;
        }

        this.pendingAddLayerFromSelectionContext = {
            startSec,
            endSec,
            createdAtMs: Date.now(),
        };
    }

    consumePendingAddLayerFromSelectionContext(
        maxAgeMs: number = 60_000,
    ): { startSec: number; endSec: number } | null {
        const ctx = this.pendingAddLayerFromSelectionContext;
        this.pendingAddLayerFromSelectionContext = null;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) return null;
        return { startSec: ctx.startSec, endSec: ctx.endSec };
    }

    peekPendingAddLayerFromSelectionContext(
        maxAgeMs: number = 60_000,
    ): { startSec: number; endSec: number } | null {
        const ctx = this.pendingAddLayerFromSelectionContext;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) return null;
        return { startSec: ctx.startSec, endSec: ctx.endSec };
    }

    setPendingPlaceholderReplaceContext(ctx: { placeholderClipId: string } | null) {
        if (!ctx) {
            this.pendingPlaceholderReplaceContext = null;
            return;
        }
        // Only one "pending sidebar action" should be active at a time.
        this.pendingAddLayerContext = null;
        this.pendingAddLayerFromSelectionContext = null;
        this.pendingSceneLayoutPickerContext = null;
        this.pendingInsertIntoScriptContext = null;
        this.pendingAddSceneFromSelectionContext = null;
        const placeholderClipId = String(ctx.placeholderClipId ?? "");
        if (!placeholderClipId) {
            this.pendingPlaceholderReplaceContext = null;
            return;
        }
        this.pendingPlaceholderReplaceContext = {
            placeholderClipId,
            createdAtMs: Date.now(),
        };
    }

    setPendingInsertIntoScriptContext(
        ctx: {
            requestId?: string;
            atClipLocalSec: number;
            atTimelineSec?: number;
            preferExistingEmptyScene?: boolean;
            mode?: PendingInsertIntoScriptMode;
            source?: string;
            sceneIndex?: number;
            sceneStartSec?: number;
            sceneEndSec?: number;
            expectedScriptClipId?: string;
            expectedCompositionSessionVersion?: number;
            expectedScriptTimingFingerprint?: string;
        } | null
    ) {
        if (!ctx) {
            this.pendingInsertIntoScriptContext = null;
            return;
        }
        // Only one "pending sidebar action" should be active at a time.
        this.pendingAddLayerContext = null;
        this.pendingAddLayerFromSelectionContext = null;
        this.pendingPlaceholderReplaceContext = null;
        this.pendingSceneLayoutPickerContext = null;
        this.pendingAddSceneFromSelectionContext = null;

        const at = Number(ctx.atClipLocalSec ?? 0);
        if (!Number.isFinite(at) || at < 0) {
            this.pendingInsertIntoScriptContext = null;
            return;
        }
        const requestId = String(ctx.requestId ?? "").trim();
        const expectedScriptClipId = String(ctx.expectedScriptClipId ?? "").trim();
        const expectedScriptTimingFingerprint = String(
            ctx.expectedScriptTimingFingerprint ?? "",
        );
        const expectedCompositionSessionVersion = Number(
            ctx.expectedCompositionSessionVersion ?? NaN,
        );
        const atTimelineSec = Number(ctx.atTimelineSec ?? NaN);
        this.pendingInsertIntoScriptContext = {
            requestId:
                requestId ||
                globalThis.crypto?.randomUUID?.() ||
                `script_insert_${Date.now()}_${Math.random()}`,
            atClipLocalSec: at,
            atTimelineSec: Number.isFinite(atTimelineSec)
                ? Math.max(0, atTimelineSec)
                : undefined,
            preferExistingEmptyScene: Boolean(ctx.preferExistingEmptyScene),
            mode:
                ctx.mode === "add_to_scene" || ctx.mode === "insert_into_script"
                    ? ctx.mode
                    : "insert_into_script",
            source: typeof ctx.source === "string" ? ctx.source : undefined,
            sceneIndex:
                Number.isFinite(Number(ctx.sceneIndex)) && Number(ctx.sceneIndex) >= 0
                    ? Math.floor(Number(ctx.sceneIndex))
                    : undefined,
            sceneStartSec:
                Number.isFinite(Number(ctx.sceneStartSec)) && Number(ctx.sceneStartSec) >= 0
                    ? Number(ctx.sceneStartSec)
                    : undefined,
            sceneEndSec:
                Number.isFinite(Number(ctx.sceneEndSec)) && Number(ctx.sceneEndSec) >= 0
                    ? Number(ctx.sceneEndSec)
                    : undefined,
            expectedScriptClipId: expectedScriptClipId || undefined,
            expectedCompositionSessionVersion: Number.isFinite(
                expectedCompositionSessionVersion,
            )
                ? expectedCompositionSessionVersion
                : undefined,
            expectedScriptTimingFingerprint:
                expectedScriptTimingFingerprint || undefined,
            createdAtMs: Date.now(),
        };
    }

    setPendingAddSceneFromSelectionContext(ctx: { startSec: number; endSec: number } | null) {
        if (!ctx) {
            this.pendingAddSceneFromSelectionContext = null;
            return;
        }
        // Only one "pending sidebar action" should be active at a time.
        this.pendingAddLayerContext = null;
        this.pendingAddLayerFromSelectionContext = null;
        this.pendingPlaceholderReplaceContext = null;
        this.pendingSceneLayoutPickerContext = null;
        this.pendingInsertIntoScriptContext = null;

        const startSec = Number(ctx.startSec);
        const endSec = Number(ctx.endSec);
        if (!Number.isFinite(startSec) || !Number.isFinite(endSec)) {
            this.pendingAddSceneFromSelectionContext = null;
            return;
        }
        if (endSec <= startSec) {
            this.pendingAddSceneFromSelectionContext = null;
            return;
        }
        this.pendingAddSceneFromSelectionContext = {
            startSec,
            endSec,
            createdAtMs: Date.now(),
        };
    }

    consumePendingAddSceneFromSelectionContext(
        maxAgeMs: number = 60_000,
    ): { startSec: number; endSec: number } | null {
        const ctx = this.pendingAddSceneFromSelectionContext;
        this.pendingAddSceneFromSelectionContext = null;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) return null;
        return { startSec: ctx.startSec, endSec: ctx.endSec };
    }

    setScriptChunkClipboard(chunk: ScriptChunkV1 | null) {
        if (!chunk) {
            this.scriptChunkClipboard = null;
            return;
        }
        this.scriptChunkClipboard = { chunk, createdAtMs: Date.now() };
    }

    getScriptChunkClipboard(): ScriptChunkV1 | null {
        return this.scriptChunkClipboard?.chunk ?? null;
    }

    consumePendingInsertIntoScriptContext(
        maxAgeMs: number = PENDING_SCRIPT_MEDIA_REQUEST_MAX_AGE_MS,
        expectedRequestId?: string,
    ): {
        requestId: string;
        atClipLocalSec: number;
        atTimelineSec?: number;
        preferExistingEmptyScene?: boolean;
        mode?: PendingInsertIntoScriptMode;
        source?: string;
        sceneIndex?: number;
        sceneStartSec?: number;
        sceneEndSec?: number;
        expectedScriptClipId?: string;
        expectedCompositionSessionVersion?: number;
        expectedScriptTimingFingerprint?: string;
    } | null {
        const ctx = this.pendingInsertIntoScriptContext;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) {
            this.pendingInsertIntoScriptContext = null;
            return null;
        }
        const expected = String(expectedRequestId ?? "").trim();
        if (expected && ctx.requestId !== expected) return null;
        this.pendingInsertIntoScriptContext = null;
        return {
            requestId: ctx.requestId,
            atClipLocalSec: ctx.atClipLocalSec,
            atTimelineSec: ctx.atTimelineSec,
            preferExistingEmptyScene: ctx.preferExistingEmptyScene,
            mode: ctx.mode,
            source: ctx.source,
            sceneIndex: ctx.sceneIndex,
            sceneStartSec: ctx.sceneStartSec,
            sceneEndSec: ctx.sceneEndSec,
            expectedScriptClipId: ctx.expectedScriptClipId,
            expectedCompositionSessionVersion: ctx.expectedCompositionSessionVersion,
            expectedScriptTimingFingerprint: ctx.expectedScriptTimingFingerprint,
        };
    }

    peekPendingInsertIntoScriptContext(
        maxAgeMs: number = PENDING_SCRIPT_MEDIA_REQUEST_MAX_AGE_MS,
    ): {
        requestId: string;
        atClipLocalSec: number;
        atTimelineSec?: number;
        preferExistingEmptyScene?: boolean;
        mode?: PendingInsertIntoScriptMode;
        source?: string;
        sceneIndex?: number;
        sceneStartSec?: number;
        sceneEndSec?: number;
        expectedScriptClipId?: string;
        expectedCompositionSessionVersion?: number;
        expectedScriptTimingFingerprint?: string;
    } | null {
        const ctx = this.pendingInsertIntoScriptContext;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) {
            this.pendingInsertIntoScriptContext = null;
            return null;
        }
        return {
            requestId: ctx.requestId,
            atClipLocalSec: ctx.atClipLocalSec,
            atTimelineSec: ctx.atTimelineSec,
            preferExistingEmptyScene: ctx.preferExistingEmptyScene,
            mode: ctx.mode,
            source: ctx.source,
            sceneIndex: ctx.sceneIndex,
            sceneStartSec: ctx.sceneStartSec,
            sceneEndSec: ctx.sceneEndSec,
            expectedScriptClipId: ctx.expectedScriptClipId,
            expectedCompositionSessionVersion: ctx.expectedCompositionSessionVersion,
            expectedScriptTimingFingerprint: ctx.expectedScriptTimingFingerprint,
        };
    }

    peekPendingPlaceholderReplaceContext(maxAgeMs: number = 60_000): { placeholderClipId: string } | null {
        const ctx = this.pendingPlaceholderReplaceContext;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) {
            this.pendingPlaceholderReplaceContext = null;
            return null;
        }
        return { placeholderClipId: ctx.placeholderClipId };
    }

    consumePendingPlaceholderReplaceContext(maxAgeMs: number = 60_000): { placeholderClipId: string } | null {
        const ctx = this.pendingPlaceholderReplaceContext;
        this.pendingPlaceholderReplaceContext = null;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) return null;
        return { placeholderClipId: ctx.placeholderClipId };
    }

    setPendingSceneLayoutPickerContext(ctx: { sceneIndex: number } | null) {
        if (!ctx) {
            this.pendingSceneLayoutPickerContext = null;
            return;
        }
        // Only one "pending sidebar action" should be active at a time.
        this.pendingAddLayerContext = null;
        this.pendingAddLayerFromSelectionContext = null;
        this.pendingPlaceholderReplaceContext = null;
        this.pendingInsertIntoScriptContext = null;
        const sceneIndex = Number(ctx.sceneIndex);
        if (!Number.isFinite(sceneIndex) || sceneIndex < 0) {
            this.pendingSceneLayoutPickerContext = null;
            return;
        }
        this.pendingSceneLayoutPickerContext = {
            sceneIndex,
            createdAtMs: Date.now(),
        };
    }

    peekPendingSceneLayoutPickerContext(maxAgeMs: number = 60_000): { sceneIndex: number } | null {
        const ctx = this.pendingSceneLayoutPickerContext;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) {
            this.pendingSceneLayoutPickerContext = null;
            return null;
        }
        return { sceneIndex: ctx.sceneIndex };
    }

    consumePendingSceneLayoutPickerContext(maxAgeMs: number = 60_000): { sceneIndex: number } | null {
        const ctx = this.pendingSceneLayoutPickerContext;
        this.pendingSceneLayoutPickerContext = null;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) return null;
        return { sceneIndex: ctx.sceneIndex };
    }

    setPendingStudioSoundAutoEnableClipId(clipId: string | null) {
        const resolvedClipId = String(clipId ?? "").trim();
        this.pendingStudioSoundAutoEnableClipId = resolvedClipId || null;
    }

    consumePendingStudioSoundAutoEnableClipId(): string | null {
        const clipId = this.pendingStudioSoundAutoEnableClipId;
        this.pendingStudioSoundAutoEnableClipId = null;
        return clipId ? String(clipId) : null;
    }

    /**
     * Set the selected visual element on the canvas.
     * This also sets selectedClipId to the parent clip.
     * For ScriptLayerClips, also sets selectedSceneIndex based on current playhead.
     */
    setSelectedVisualElement(ref: VisualElementRef | null) {
        if (!ref) {
            this.clearSelectedVisualElement();
            return;
        }

        // Ensure clip selection + selection clearing semantics match setSelectedClipId.
        // This prevents stale scene/range selection from lingering when selecting a non-script visual on the canvas.
        this.setSelectedClipId(ref.clipId);
        this.selectedVisualElement = ref;
        this.setSelectedTransitionId(null);

        // For ScriptLayerClips, determine and set scene index based on playhead
        try {
            const clip: any = CompositionService.getClipById(ref.clipId);
            if (clip && String(clip.type) === 'script') {
                const EPS = 1e-3;
                const clipStartSec = Number(clip.start?.seconds ?? 0);
                const rate = Number(clip.playbackRate ?? 1) || 1;

                // Timeline duration is in "timeline seconds" (WaveSurfer domain).
                const dur = Number(
                    CompositionStateStore.clips?.[ref.clipId]?.trimmedDuration ??
                        clip.timelineDuration?.seconds ??
                        0
                );
                if (!Number.isFinite(dur) || dur <= EPS) return;

                const inSec = Number(clip.range?.[0]?.seconds ?? 0);
                const outSec = Number(clip.range?.[1]?.seconds ?? clip.duration?.seconds ?? inSec);

				const rawSegments = clip.scriptSegments;
				const hasAuthoritativeSegments = Array.isArray(rawSegments);
				const segments: any[] = hasAuthoritativeSegments ? rawSegments : [];
				const mapSourceToTimeline = (srcTime: number): number | null => {
					if (!Number.isFinite(srcTime)) return null;

					if (!segments.length) {
						if (hasAuthoritativeSegments) return null;
						if (srcTime < inSec - EPS || srcTime > outSec + EPS) return null;
						if (Math.abs(srcTime - outSec) <= EPS) return dur;
						return Math.max(0, (srcTime - inSec) / rate);
					}

					if (Math.abs(srcTime - outSec) <= EPS) {
						let lastPlayableEnd = Number.NEGATIVE_INFINITY;
						for (const segment of segments) {
							if (segment?.isGap) continue;
							const sourceStart = Number(segment?.srcStartSec ?? Number.NaN);
							const sourceEnd = Number(segment?.srcEndSec ?? Number.NaN);
							const compositionStart = Number(segment?.compStartSec ?? Number.NaN);
							const segmentRateRaw = Number(segment?.playbackRate ?? rate);
							const segmentRate =
								Number.isFinite(segmentRateRaw) && segmentRateRaw > 0
									? segmentRateRaw
									: 1;
							if (
								!Number.isFinite(sourceStart) ||
								!Number.isFinite(sourceEnd) ||
								!Number.isFinite(compositionStart) ||
								sourceEnd <= sourceStart
							) {
								continue;
							}
							const compositionEnd =
								compositionStart + (sourceEnd - sourceStart) / segmentRate;
							if (Number.isFinite(compositionEnd)) {
								lastPlayableEnd = Math.max(lastPlayableEnd, compositionEnd);
							}
						}
						return Number.isFinite(lastPlayableEnd)
							? lastPlayableEnd - clipStartSec
							: null;
					}

					for (const seg of segments) {
						if (seg?.isGap) continue;
						const s0 = Number(seg?.srcStartSec ?? NaN);
                        const s1 = Number(seg?.srcEndSec ?? NaN);
                        const c0 = Number(seg?.compStartSec ?? NaN);
                        if (!Number.isFinite(s0) || !Number.isFinite(s1) || !Number.isFinite(c0)) continue;
                        if (srcTime >= s0 && srcTime < s1) {
							const segRateRaw = Number(seg?.playbackRate ?? rate);
							const segRate = Number.isFinite(segRateRaw) && segRateRaw > 0 ? segRateRaw : 1;
                            const absCompTime = c0 + (srcTime - s0) / segRate;
                            return absCompTime - clipStartSec;
                        }
                    }

                    // A scene boundary may sit inside removed source material.
                    // Snap it to the next playable segment so every entry path
                    // resolves the same semantic section as the timeline itself.
					const nextSegment = segments
						.filter((segment: any) => {
							if (segment?.isGap) return false;
							const sourceStart = Number(segment?.srcStartSec ?? Number.NaN);
                            return Number.isFinite(sourceStart) && sourceStart + EPS >= srcTime;
                        })
                        .sort(
                            (first: any, second: any) =>
                                Number(first.srcStartSec) - Number(second.srcStartSec),
                        )[0];
                    if (nextSegment && Number.isFinite(Number(nextSegment?.compStartSec))) {
						return Number(nextSegment.compStartSec) - clipStartSec;
                    }

                    return null;
                };

                const model = buildScriptTimelineModel({
                    clip,
                    duration: dur,
                    mapSourceBoundaryToTimeline: mapSourceToTimeline,
                });

                // Find which section contains the playhead (timeline domain).
                const timelineTime = Number(CompositionStateStore.currentTime ?? 0) - clipStartSec;
                const t = Math.max(0, Math.min(dur - EPS, Number.isFinite(timelineTime) ? timelineTime : 0));
                const section = findScriptTimelineSectionAtTime(model.sections, t);
                if (!section) return;
                this.selectScriptTimelineSection(section, model.sections);
            } else {
                // For non-script visuals, ensure script-scoped selection is cleared.
                this.selectedSceneIndex = null;
                this.selectedScriptLayerRange = null;
                this.selectedTranscriptTimelineRange = null;
                this.selectedScriptSection = null;
            }
        } catch {
            // Ignore errors during scene lookup (e.g., if service not initialized)
        }
    }

    clearSelectedVisualElement() {
		this.clearSelectedClipId();
    }

    setEditingTextClipId(id: string | null) {
        this.editingTextClipId = id;
    }

    startEditingTextClip(clipId: string) {
        if (!clipId) return;
        this.setSelectedClipId(clipId);
        this.editingTextClipId = clipId;
    }

    setSelectedTransitionId(id: string | null) {
        this.selectedTransitionId = id;
    }

    setDraggingObject(draggingObject: DraggingObjectPayload | null) {
        this.draggingObject = draggingObject;
    }

    setRangeToolEnabled(value: boolean) {
        if (this.rangeToolEnabled !== value) {
            this.selectedScriptLayerRange = null;
        }
        this.rangeToolEnabled = value;
        if (value) this.slipToolEnabled = false;
    }

    setSelectedRange(value: {start: number, end: number}) {
        this.selectedRange = value;
    }

    setSlipToolEnabled(value: boolean) {
        this.slipToolEnabled = value;
        if (value) {
            this.rangeToolEnabled = false;
            this.selectedScriptLayerRange = null;
        }
    }

    /**
     * Set selected range on script layer (for range tool selection).
     * Does NOT set scene index - use setSelectedSceneIndex for scene clicks.
     */
    setSelectedScriptLayerRange(value: {start: number, end: number} | null) {
        this.selectedScriptLayerRange = value;
        // Note: Do NOT set selectedSceneIndex here - range selection is independent
    }

    /** Store a text-owned transcript selection without changing the selected scene. */
    setSelectedTranscriptTimelineRange(value: {start: number, end: number} | null) {
        this.selectedTranscriptTimelineRange = value;
    }

    /**
     * Ask the transcript editor to select a timeline range. The request is
     * revisioned so reselecting an already-selected scene still takes effect.
     */
    requestTranscriptTimelineSelection(value: {start: number, end: number} | null) {
        this.scriptTranscriptSelectionRequest = {
            range: value ? { start: value.start, end: value.end } : null,
            revision: this.scriptTranscriptSelectionRequest.revision + 1,
        };
    }

    /** Atomically select one semantic section of the ScriptClip spine. */
    selectScriptTimelineSection(
        section: ScriptTimelineSection,
        sections: ScriptTimelineSection[] = [section],
    ) {
        this.selectedScriptSection = { ...section };
        this.selectedSceneIndex = section.sceneIndex;
        this.selectedTranscriptTimelineRange = null;
        this.requestTranscriptTimelineSelection(
            getScriptTimelineSelectionRange(sections, section),
        );
    }

    clearSelectedScriptTimelineSection() {
        this.selectedScriptSection = null;
        this.selectedSceneIndex = null;
		this.selectedTranscriptTimelineRange = null;
		this.requestTranscriptTimelineSelection(null);
    }

    /**
     * Set selected scene by index (for scene clicks).
     * An optional timeline-domain range is forwarded to the transcript editor.
     */
    setSelectedSceneIndex(index: number | null, sceneRange?: {start: number, end: number}) {
        this.selectedSceneIndex = index;
        this.selectedScriptSection = null;
        this.selectedTranscriptTimelineRange = null;
        // Keep compatibility for callers that already provide timeline-domain bounds.
        if (index !== null && sceneRange) {
            this.requestTranscriptTimelineSelection(sceneRange);
        } else {
            this.requestTranscriptTimelineSelection(null);
        }
    }

    setTheme(value: ThemeVariants = "dark") {
        if (value === "system") {
            const isDarkTheme = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
            ApplicationStore.theme = isDarkTheme ? "dark" : "light";
        } else {
            ApplicationStore.theme = value;
        }
    }

    setSceneEditScope(scope: SceneEditScope) {
        this.sceneEditScope = scope;
    }

    setFiltersPath(path: string | null) {
        this.filtersPath = path;
    }

    setEffectsPath(path: string | null) {
        this.effectsPath = path;
    }

    setTransitionsPath(path: string | null) {
        this.transitionsPath = path;
    }

    setSubtitleStylesPath(path: string | null) {
        this.subtitleStylesPath = path;
    }

    setAnimationsPath(path: string | null) {
        this.animationsPath = path;
    }

    setTitlesPath(path: string | null) {
        this.titlesPath = path;
    }

    setTransitions(transitions: Record<string, TransitionDescription>) {
        this.transitions = transitions;
    }

    setFilters(filters: Record<string, FilterDescription>) {
        this.filters = filters;
    }

    setEffects(effects: Record<string, EffectDescription>) {
        this.effects = effects;
    }

    // Crop mode actions
    enterCropMode(initialCrop?: CropConfig) {
        if (!this.selectedVisualElement) return;
        this.isCropMode = true;
        // Use provided initial crop (from clip's current crop) or default to full frame
        this.workingCrop = initialCrop ?? { x: 0, y: 0, width: 1, height: 1 };
        this.cropZoom = 1;
    }

    exitCropMode() {
        this.isCropMode = false;
    }

    setCropScope(scope: CropScope) {
        this.cropScope = scope;
    }

    setWorkingCrop(crop: CropConfig) {
        this.workingCrop = { ...crop };
    }

    setCropZoom(zoom: number) {
        this.cropZoom = Math.max(0.1, Math.min(5, zoom));
    }

    resetCrop() {
        this.workingCrop = { x: 0, y: 0, width: 1, height: 1 };
        this.cropZoom = 1;
    }

    reset() {
        this.isLoading = true;
        this.isRendering = false;
        this.selectedClipId = null;
        this.selectedVisualElement = null;
        this.selectedTransitionId = null;
        this.subtitlesClipId = null;
        this.transitions = {};
        this.filters = {};
        this.effects = {};
        this.draggingObject = null;
        this.rangeToolEnabled = false;
        this.slipToolEnabled = false;
        this.selectedScriptLayerRange = null;
        this.selectedTranscriptTimelineRange = null;
        this.selectedScriptSection = null;
        this.scriptTranscriptSelectionRequest = { range: null, revision: 0 };
        this.selectedSceneIndex = null;
        this.theme = "system";
        this.filtersPath = null;
        this.effectsPath = null;
        this.transitionsPath = null;
        this.titlesPath = null;
        this.subtitleStylesPath = null;
        this.animationsPath = null;
        // Reset crop state
        this.isCropMode = false;
        this.cropScope = 'current';
        this.workingCrop = { x: 0, y: 0, width: 1, height: 1 };
        this.cropZoom = 1;
    }
}

const ApplicationStore = new ApplicationStoreInstance();

export { ApplicationStore };
